import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MICRO_OVERRIDE_CHIPS,
  setInsightTarget,
  targetDisplayName,
  uploadInsightCapture,
} from '../../utils/insightApi';
import { fileToJpegDataUrl } from '../../utils/imageFileUtils';
import {
  isNyafazNativeMicroscopeAvailable,
  captureNativeMicroscopeInline,
  startNativeScalpInlinePreview,
  updateNativeScalpInlineRect,
  stopNativeScalpInlinePreview,
  measurePreviewRect,
  supportsNativeInlineScalpPreview,
  listNativeUsbCameras,
} from '../../../../../enterprise/shared/nyafazNativeBridge';
import EnterpriseCameraSelect from '../../../../../enterprise/shared/EnterpriseCameraSelect';
import {
  parseCameraSelectValue,
  resolveMacroCameraSelectValue,
} from '../../../../../enterprise/shared/cameraSelectValues';
import {
  isLegacyCustomAndroidWebView,
  listMicroscopeVideoInputs,
  openBuiltinFaceCameraStream,
  openMicroscopeVideoStream,
  readStoredMacroUsbDeviceId,
  storeMacroUsbDeviceId,
  waitForPreviewLayout,
} from '../../../../../enterprise/shared/usbMicroscopeCamera';
import { pathWithSessionId } from '../../utils/sessionManager';
import { hairAppPath, resolveInsightSessionId, setHairInsightSessionId } from '../../config/hairApp';
import './HairInsight.css';

const HAIR_PUBLIC = `${process.env.PUBLIC_URL || ''}/hair`;

const ADVANCED_SEQUENCE = ['hairline', 'top', 'crown'];

function advancedRecAfterView(completedView) {
  const idx = ADVANCED_SEQUENCE.indexOf(completedView);
  if (idx < 0 || idx >= ADVANCED_SEQUENCE.length - 1) return null;
  const nextView = ADVANCED_SEQUENCE[idx + 1];
  const total = ADVANCED_SEQUENCE.length;
  const reasons = {
    top: 'Capture a close scalp look at the top of the head.',
    crown: 'Capture a close scalp look at the crown.',
  };
  return {
    needsAdditionalCapture: true,
    source: 'microscope',
    target: nextView,
    reason: reasons[nextView] || `Capture a close scalp look at the ${targetDisplayName(nextView).toLowerCase()}.`,
    priority: 'high',
    step: idx + 2,
    totalSteps: total,
  };
}

const SCALP_CAPTURE_GUIDE = {
  hairline: {
    zoneImg: `${HAIR_PUBLIC}/scalp-zone-hairline.png`,
    refImg: `${HAIR_PUBLIC}/scalp-ref-hairline.png`,
    lede: 'Place the USB microscope on the front hairline where hair meets the forehead.',
    refCaption: 'Hairline reference',
  },
  top: {
    zoneImg: `${HAIR_PUBLIC}/scalp-zone-top.png`,
    refImg: `${HAIR_PUBLIC}/scalp-ref-top.png`,
    lede: 'Capture the center part at the top of the head — show scalp and follicles clearly.',
    refCaption: 'Top reference',
  },
  crown: {
    zoneImg: `${HAIR_PUBLIC}/scalp-zone-crown.png`,
    refImg: `${HAIR_PUBLIC}/scalp-ref-crown.png`,
    lede: 'Center on the crown whorl — the spiral where hair grows from the vertex.',
    refCaption: 'Crown reference',
  },
};

function scalpGuideForTarget(target) {
  if (target === 'hairline' || target === 'left_temple' || target === 'right_temple') {
    return SCALP_CAPTURE_GUIDE.hairline;
  }
  if (target === 'crown') return SCALP_CAPTURE_GUIDE.crown;
  return SCALP_CAPTURE_GUIDE.top;
}

const SCALP_TIPS = [
  { id: 'focus', label: 'Center the target area in the frame.' },
  { id: 'steady', label: 'Hold the microscope steady.' },
  { id: 'light', label: 'Use bright, even lighting.' },
  { id: 'contact', label: 'Gently rest the lens on the scalp.' },
];

function IconBack() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

export default function HairInsightMicroscopeCapture() {
  const navigate = useNavigate();
  const location = useLocation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);
  const previewFrameRef = useRef(null);

  const sessionId = resolveInsightSessionId(location, null);
  const scanMode = location.state?.scanMode || 'advanced';
  const isAdvanced = scanMode === 'advanced' || scanMode === 'detailed';
  const recommended =
    location.state?.recommendedTarget ||
    location.state?.captureRecommendation?.target ||
    (isAdvanced ? 'hairline' : 'scalp_detail');

  const [phase, setPhase] = useState('capture');
  const [target, setTarget] = useState(recommended);
  const [selectionSource, setSelectionSource] = useState('ai');
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [webPreviewReady, setWebPreviewReady] = useState(false);
  const [needsCameraPick, setNeedsCameraPick] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [micUnavailable, setMicUnavailable] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [frozen, setFrozen] = useState(false);
  const [step, setStep] = useState(
    () => location.state?.captureRecommendation?.step || ADVANCED_SEQUENCE.indexOf(recommended) + 1 || 1,
  );
  const [totalSteps, setTotalSteps] = useState(
    () => location.state?.captureRecommendation?.totalSteps || (isAdvanced ? 3 : 1),
  );
  const [useNativeUsb, setUseNativeUsb] = useState(false);
  const [nativeUsbDevices, setNativeUsbDevices] = useState([]);
  const [selectedNativeUsbId, setSelectedNativeUsbId] = useState('');
  const [webFacing, setWebFacing] = useState('front');
  const [nativeOpening, setNativeOpening] = useState(false);
  const [nativeCooldown, setNativeCooldown] = useState(false);

  useEffect(() => {
    if (sessionId) setHairInsightSessionId(sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      navigate(hairAppPath('/insight/capture'), { replace: true, state: { scanMode } });
    }
  }, [sessionId, navigate, scanMode]);

  useEffect(() => {
    if (phase !== 'capture' || preview) return undefined;
    let cancelled = false;
    (async () => {
      if (!isNyafazNativeMicroscopeAvailable()) return;
      const cams = await listNativeUsbCameras(4500);
      if (!cancelled) setNativeUsbDevices(cams);
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, preview]);

  useEffect(() => {
    if (preview || !useNativeUsb) {
      try {
        stopNativeScalpInlinePreview();
      } catch {
        /* ignore */
      }
    }
  }, [preview, useNativeUsb]);

  // Native USB: live preview overlays this page (no separate capture popup).
  useEffect(() => {
    if (!useNativeUsb || preview || !selectedNativeUsbId) return undefined;
    let cancelled = false;

    const pushPreview = async () => {
      const rect = await waitForPreviewLayout(() => previewFrameRef.current, { timeoutMs: 4000 });
      if (cancelled) return;
      try {
        if (supportsNativeInlineScalpPreview()) {
          startNativeScalpInlinePreview({
            target,
            label: targetDisplayName(target),
            rect,
            nativeId: Number(selectedNativeUsbId),
          });
        }
      } catch {
        /* bridge not ready yet */
      }
    };

    const syncRect = () => {
      const rect = measurePreviewRect(previewFrameRef.current);
      if (rect) updateNativeScalpInlineRect(rect);
    };

    pushPreview();
    const t1 = window.setTimeout(pushPreview, 900);
    window.addEventListener('resize', syncRect);
    window.addEventListener('orientationchange', syncRect);
    const ro =
      typeof ResizeObserver !== 'undefined' && previewFrameRef.current
        ? new ResizeObserver(syncRect)
        : null;
    if (previewFrameRef.current && ro) ro.observe(previewFrameRef.current);

    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.removeEventListener('resize', syncRect);
      window.removeEventListener('orientationchange', syncRect);
      ro?.disconnect();
    };
  }, [useNativeUsb, target, preview, selectedNativeUsbId]);

  useEffect(() => () => {
    try {
      stopNativeScalpInlinePreview();
    } catch {
      /* ignore */
    }
  }, []);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setWebPreviewReady(false);
  };

  const startCamera = useCallback(async (id, facingMode) => {
    stopStream();
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setMicUnavailable(true);
        setError('Microscope unavailable. You can continue with the hair image analysis.');
        return;
      }
      const wantId = typeof id === 'string' ? id.trim() : '';
      let stream;
      if (wantId) {
        stream = await openMicroscopeVideoStream({
          deviceId: wantId,
          timeoutMs: 15000,
        });
        storeMacroUsbDeviceId(wantId);
      } else if (facingMode === 'environment' || facingMode === 'user') {
        stream = await openBuiltinFaceCameraStream({
          facing: facingMode,
          timeoutMs: 15000,
        });
        storeMacroUsbDeviceId('');
      } else {
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setMicUnavailable(false);
      setWebPreviewReady(true);
    } catch {
      setMicUnavailable(true);
      setError('Microscope unavailable. You can continue with the hair image analysis.');
    }
  }, []);

  useEffect(() => {
    if (phase !== 'capture' || useNativeUsb) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const cams = await listMicroscopeVideoInputs();
        if (cancelled) return;
        setDevices(cams);
        const usable = cams.filter((d) => d?.deviceId);
        setNeedsCameraPick(usable.length > 0);
        if (!usable.length) {
          setMicUnavailable(true);
          setError('No camera found. Connect the USB microscope via OTG, then choose a camera.');
          return;
        }
        const stored = readStoredMacroUsbDeviceId();
        const storedOk = stored && usable.some((d) => d.deviceId === stored);
        if (storedOk) {
          setDeviceId(stored);
          if (!cancelled) await startCamera(stored);
        } else {
          setDeviceId('');
        }
        setMicUnavailable(false);
        setError(null);
        if (usable.length > 1 && isLegacyCustomAndroidWebView()) {
          setNeedsCameraPick(true);
        }
      } catch {
        if (!cancelled) {
          setMicUnavailable(true);
          setError('Microscope unavailable. You can continue with the hair image analysis.');
        }
      }
    })();
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [phase, useNativeUsb, startCamera]);

  const macroCameraSelectValue = resolveMacroCameraSelectValue({
    useNativeUsb,
    nativeId: selectedNativeUsbId,
    deviceId,
    webFacing,
  });

  const handleHairCameraSelectChange = useCallback(
    async (rawValue) => {
      const parsed = parseCameraSelectValue(rawValue);
      if (parsed.kind === 'none') return;
      setError(null);
      stopStream();

      if (parsed.kind === 'native') {
        if (!isNyafazNativeMicroscopeAvailable()) {
          setError('USB microscope (Salon app) is not available.');
          return;
        }
        try {
          stopNativeScalpInlinePreview();
        } catch {
          /* ignore */
        }
        setUseNativeUsb(true);
        setDeviceId('');
        setSelectedNativeUsbId(parsed.nativeId);
        return;
      }

      try {
        stopNativeScalpInlinePreview();
      } catch {
        /* ignore */
      }
      setUseNativeUsb(false);
      setSelectedNativeUsbId('');

      if (parsed.kind === 'device') {
        setDeviceId(parsed.deviceId);
        storeMacroUsbDeviceId(parsed.deviceId);
        await startCamera(parsed.deviceId);
        return;
      }

      const nextFacing = parsed.facing === 'rear' ? 'rear' : 'front';
      setWebFacing(nextFacing);
      setDeviceId('');
      const mode = nextFacing === 'rear' ? 'environment' : 'user';
      await startCamera('', mode);
    },
    [startCamera],
  );

  const refreshHairCameras = useCallback(async () => {
    setError(null);
    try {
      if (isNyafazNativeMicroscopeAvailable()) {
        const nativeCams = await listNativeUsbCameras(4500);
        setNativeUsbDevices(nativeCams);
      }
    } catch {
      /* ignore */
    }
    try {
      const cams = await listMicroscopeVideoInputs();
      setDevices(cams);
      const usable = cams.filter((d) => d?.deviceId);
      setNeedsCameraPick(usable.length > 0);
      if (!usable.length && !isNyafazNativeMicroscopeAvailable()) {
        setMicUnavailable(true);
        setError('No camera found. Connect the USB microscope via OTG, then tap Refresh cameras.');
      } else {
        setMicUnavailable(false);
      }
    } catch {
      setError('Could not refresh cameras. Check OTG connection and try again.');
    }
  }, []);

  const onSelectChip = async (id) => {
    if (isAdvanced) return;
    setTarget(id);
    setSelectionSource('stylist');
    setPreview(null);
    try {
      if (sessionId) await setInsightTarget(sessionId, id);
    } catch {
      /* non-blocking */
    }
  };

  const snap = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.filter = `brightness(${brightness}%)`;
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL('image/jpeg', 0.9);
  };

  const goAnalyze = useCallback(
    (partial) => {
      navigate(pathWithSessionId('/insight/analyzing', sessionId), {
        state: {
          insightSessionId: sessionId,
          scanMode,
          partialAnalysis: partial,
          token: location.state?.token,
        },
      });
    },
    [navigate, sessionId, scanMode, location.state?.token],
  );

  const applyNextCaptureRec = useCallback((rec) => {
    if (!rec?.target) return false;
    setTarget(rec.target);
    setSelectionSource('ai');
    setPreview(null);
    setError(null);
    if (rec.step) setStep(rec.step);
    if (rec.totalSteps) setTotalSteps(rec.totalSteps);
    setPhase('capture');
    setNativeCooldown(true);
    window.setTimeout(() => setNativeCooldown(false), 900);
    return true;
  }, []);

  const advanceOrAnalyze = useCallback(
    (rec, completedView) => {
      if (rec?.needsAdditionalCapture && rec?.target) {
        applyNextCaptureRec(rec);
        return;
      }
      if (isAdvanced && completedView) {
        const local = advancedRecAfterView(completedView);
        if (local) {
          applyNextCaptureRec(local);
          return;
        }
      }
      goAnalyze(false);
    },
    [goAnalyze, isAdvanced, applyNextCaptureRec],
  );

  const stageCapturePreview = useCallback(async (dataUrl) => {
    setBusy(true);
    setError(null);
    try {
      setPreview(dataUrl);
    } catch (e) {
      setError(e?.message || 'Could not prepare image');
    } finally {
      setBusy(false);
      setNativeOpening(false);
    }
  }, []);

  const confirmPendingCapture = useCallback(async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const completedView = target;
      const res = await uploadInsightCapture(sessionId, {
        source: 'microscope',
        view: completedView,
        image: preview,
        selectionSource,
      });
      const rec = res.captureRecommendation;
      setPreview(null);
      advanceOrAnalyze(rec, completedView);
    } catch (e) {
      setError(e.message || 'Could not save capture');
    } finally {
      setBusy(false);
    }
  }, [sessionId, target, selectionSource, preview, advanceOrAnalyze]);

  const captureUsbInline = useCallback(async () => {
    if (!isNyafazNativeMicroscopeAvailable()) {
      setUseNativeUsb(false);
      setError('USB microscope bridge not available — trying device camera…');
      return;
    }
    setNativeOpening(true);
    setError(null);
    setMicUnavailable(false);
    try {
      const rect = await waitForPreviewLayout(() => previewFrameRef.current, { timeoutMs: 2500 });
      if (supportsNativeInlineScalpPreview()) {
        startNativeScalpInlinePreview({
          target,
          label: targetDisplayName(target),
          rect,
        });
      }
      const dataUrl = await captureNativeMicroscopeInline({
        target,
        label: targetDisplayName(target),
        preferInline: true,
        timeoutMs: supportsNativeInlineScalpPreview() ? 20000 : 45000,
      });
      await stageCapturePreview(dataUrl);
    } catch (e) {
      setNativeOpening(false);
      const msg = e?.message || '';
      if (msg.includes('timed out') || msg.includes('cancel')) {
        try {
          stopNativeScalpInlinePreview();
        } catch {
          /* ignore */
        }
        setUseNativeUsb(false);
        setError('USB capture timed out on this device. Switched to camera fallback — tap Capture again.');
        return;
      }
      setError(msg || 'USB microscope capture failed');
      setMicUnavailable(true);
      try {
        stopNativeScalpInlinePreview();
      } catch {
        /* ignore */
      }
      setUseNativeUsb(false);
    }
  }, [stageCapturePreview, target]);

  const onCapture = async () => {
    if (useNativeUsb) {
      await captureUsbInline();
      return;
    }
    const dataUrl = snap();
    if (!dataUrl) {
      setError('Microscope unavailable. You can continue with the hair image analysis.');
      return;
    }
    await stageCapturePreview(dataUrl);
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await fileToJpegDataUrl(file, { maxSide: 1600, quality: 0.9 });
      await stageCapturePreview(dataUrl);
    } catch (err) {
      setError(err.message || 'Upload failed');
    }
  };

  const regionName = targetDisplayName(target);
  const seqIdx = Math.max(0, ADVANCED_SEQUENCE.indexOf(target));
  const nextLabel = `Capture ${regionName}`;
  const progressLabel = isAdvanced
    ? `Scalp ${Math.min(step, totalSteps)}/${totalSteps} · ${regionName}`
    : regionName;
  const targetReason =
    location.state?.captureRecommendation?.reason ||
    `We're capturing the ${regionName.toLowerCase()} area. Place the USB microscope on the ${regionName.toLowerCase()}.`;

  const captureBusy = busy || nativeOpening || nativeCooldown;
  const isLastScalp =
    isAdvanced && seqIdx === ADVANCED_SEQUENCE.length - 1;
  const captureGuide = scalpGuideForTarget(target);

  return (
    <div className="hair-insight-page page-bg hi-capture-first hi-scalp-capture">
      <header className="hi-header hi-wide-capture__header hi-scalp-capture__header">
        <button
          type="button"
          className="hi-wide-capture__icon-btn hi-wide-capture__back"
          aria-label="Back"
          onClick={() =>
            navigate(hairAppPath('/insight/capture'), {
              state: { insightSessionId: sessionId, scanMode },
            })
          }
        >
          <IconBack />
        </button>
        <div className="hi-progress-steps hi-wide-capture__steps" aria-label="Progress">
          <span className="is-done">1. Hair Scan</span>
          <span className="is-on">2. Scalp Detail</span>
          <span>3. Results</span>
        </div>
      </header>

      {isAdvanced ? (
        <div className="hi-chip-row hi-scalp-capture__chips" role="list" aria-label="Scalp capture progress">
          {ADVANCED_SEQUENCE.map((id, i) => (
            <span
              key={id}
              className={`hi-chip ${target === id ? 'is-active' : ''} ${i < seqIdx ? 'is-done' : ''}`}
            >
              {i + 1}. {targetDisplayName(id)}
            </span>
          ))}
        </div>
      ) : (
        <div className="hi-chip-row hi-scalp-capture__chips" role="group" aria-label="Target override">
          {MICRO_OVERRIDE_CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`hi-chip ${target === c.id ? 'is-active' : ''}`}
              onClick={() => onSelectChip(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <div className="hi-layout hi-layout-capture hi-scalp-capture__layout">
        <section className="hi-preview-card hi-scalp-capture__preview">
          <h2 className="hi-scalp-capture__preview-title">Live USB Scalp Preview</h2>
          <div className="hi-scalp-capture__camera-dock">
            <EnterpriseCameraSelect
              label="Camera"
              value={macroCameraSelectValue}
              devices={devices}
              nativeDevices={isNyafazNativeMicroscopeAvailable() ? nativeUsbDevices : []}
              onChange={handleHairCameraSelectChange}
              onRefresh={refreshHairCameras}
            />
          </div>
          <div
            ref={previewFrameRef}
            className="hi-preview-frame hi-micro-frame hi-scalp-capture__frame"
          >
            {preview ? (
              <img src={preview} alt={`${regionName} capture`} className="hi-preview-media" />
            ) : useNativeUsb ? (
              <div className="hi-native-usb-panel hi-scalp-capture__native-panel">
                <p className="hi-native-usb-title">
                  {selectedNativeUsbId ? 'USB live preview' : 'Select USB microscope'}
                </p>
                <p className="hi-native-usb-copy">
                  {selectedNativeUsbId
                    ? supportsNativeInlineScalpPreview()
                      ? `Place the microscope on the ${regionName.toLowerCase()} — live view appears here. Tap Capture when ready.`
                      : 'Connect the USB microscope via OTG, then tap Capture.'
                    : 'Choose the USB microscope from the camera list above.'}
                </p>
              </div>
            ) : !webPreviewReady ? (
              <div className="hi-native-usb-panel hi-scalp-capture__native-panel">
                <p className="hi-native-usb-title">Select a camera</p>
                <p className="hi-native-usb-copy">
                  Pick front, rear, a connected camera, or USB microscope from the list above.
                </p>
              </div>
            ) : (
              <video
                ref={videoRef}
                className="hi-preview-media"
                playsInline
                muted
                autoPlay
                style={{ filter: `brightness(${brightness}%)` }}
              />
            )}
            <div className="hi-wide-capture__corners" aria-hidden>
              <i />
              <i />
              <i />
              <i />
            </div>
            <span className="hi-scalp-capture__region-label">{regionName}</span>
          </div>

          <div className="hi-scalp-capture__callout" role="status">
            <span className="hi-scalp-capture__callout-ico" aria-hidden />
            <p>{targetReason}</p>
          </div>
        </section>

        <aside className="hi-guide-card hi-scalp-capture__guide">
          <h2 className="hi-scalp-capture__guide-title">How to capture</h2>
          <p className="hi-scalp-capture__guide-sub">Follow the guide and reference below.</p>

          <div className="hi-scalp-capture__zone-block">
            <h3>What we are capturing</h3>
            <p className="hi-scalp-capture__guide-lede">{captureGuide.lede}</p>
            <figure className="hi-scalp-capture__zone">
              <img
                src={captureGuide.zoneImg}
                alt={`${regionName} placement on head`}
              />
            </figure>
          </div>

          <div className="hi-scalp-capture__refs">
            <p className="hi-scalp-capture__refs-label">
              <span aria-hidden>💡</span> Reference
            </p>
            <figure className="hi-scalp-capture__ref">
              <img
                src={captureGuide.refImg}
                alt={`${regionName} microscope reference`}
              />
              <figcaption>{captureGuide.refCaption}</figcaption>
            </figure>
          </div>

          <ul className="hi-scalp-capture__howto">
            <li>
              <span className="hi-scalp-capture__howto-ico hi-scalp-capture__howto-ico--focus" aria-hidden />
              <div>
                <strong>Focus on the {regionName.toLowerCase()}</strong>
                <p>Place the microscope on the {regionName.toLowerCase()} area.</p>
              </div>
            </li>
            <li>
              <span className="hi-scalp-capture__howto-ico hi-scalp-capture__howto-ico--steady" aria-hidden />
              <div>
                <strong>Keep it steady</strong>
                <p>Hold the device still for a clear shot.</p>
              </div>
            </li>
            <li>
              <span className="hi-scalp-capture__howto-ico hi-scalp-capture__howto-ico--light" aria-hidden />
              <div>
                <strong>Good lighting</strong>
                <p>Ensure adequate light for best clarity.</p>
              </div>
            </li>
          </ul>

          {micUnavailable && (
            <div className="hi-quality warn">
              <strong>Microscope unavailable</strong>
              <p>You can continue with the hair image analysis.</p>
            </div>
          )}
          {error && <p className="hi-error">{error}</p>}

          {!useNativeUsb && (
            <>
              <button
                type="button"
                className="hi-btn ghost hi-advanced-toggle"
                onClick={() => setAdvancedOpen((v) => !v)}
              >
                {advancedOpen ? 'Hide Advanced' : 'Advanced'}
              </button>
              {advancedOpen && (
                <div className="hi-advanced">
                  <label className="hi-field">
                    Brightness
                    <input
                      type="range"
                      min="60"
                      max="160"
                      value={brightness}
                      onChange={(e) => setBrightness(Number(e.target.value))}
                    />
                  </label>
                  <button
                    type="button"
                    className="hi-btn ghost"
                    onClick={() => {
                      if (!videoRef.current) return;
                      if (frozen) {
                        videoRef.current.play();
                        setFrozen(false);
                      } else {
                        videoRef.current.pause();
                        setFrozen(true);
                      }
                    }}
                  >
                    {frozen ? 'Unfreeze' : 'Freeze'}
                  </button>
                </div>
              )}
            </>
          )}
        </aside>

        <section className="hi-wide-capture__tips hi-scalp-capture__tips" aria-label="Tips for best results">
          <h3>Tips for best results</h3>
          <ul>
            {SCALP_TIPS.map((tip) => (
              <li key={tip.id}>
                <span className={`hi-wide-capture__tip-ico hi-wide-capture__tip-ico--${tip.id}`} aria-hidden />
                <span>{tip.label}</span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="hi-scalp-capture__notice" role="note">
        <span aria-hidden>ⓘ</span>
        <p>
          {preview
            ? 'Check the scalp close-up. Tap Confirm to save this region, or Retake to capture again.'
            : useNativeUsb
            ? supportsNativeInlineScalpPreview()
              ? 'Live USB preview stays on this screen. Keep the target area centered, then tap Capture.'
              : 'Connect the USB microscope via OTG first, then tap Capture.'
            : 'Use the USB microscope preview on the left. Keep the target area centered and steady.'}
          {isLastScalp && !preview ? ' This is the last scalp detail — then analysis starts.' : ''}
        </p>
      </div>

      <div className="hi-actions hi-actions-capture hi-scalp-capture__actions">
        {preview ? (
          <>
            <button
              type="button"
              className="hi-btn ghost"
              disabled={busy}
              onClick={() => {
                setPreview(null);
                setError(null);
              }}
            >
              Retake
            </button>
            <button
              type="button"
              className="hi-btn primary hi-btn-capture"
              disabled={busy}
              onClick={() => void confirmPendingCapture()}
            >
              {busy ? 'Saving…' : isLastScalp ? `Confirm ${regionName}` : 'Confirm & next'}
            </button>
          </>
        ) : null}

        {!preview && useNativeUsb ? (
          <button
            type="button"
            className="hi-btn primary hi-btn-capture"
            disabled={captureBusy}
            onClick={captureUsbInline}
          >
            {busy || nativeOpening ? 'Capturing…' : nativeCooldown ? 'Ready…' : nextLabel}
          </button>
        ) : !preview && !micUnavailable ? (
          <button
            type="button"
            className="hi-btn primary hi-btn-capture"
            disabled={busy}
            onClick={onCapture}
          >
            {busy ? 'Preparing…' : nextLabel}
          </button>
        ) : !preview ? (
          <button type="button" className="hi-btn primary hi-btn-capture" onClick={() => goAnalyze(true)}>
            Continue
          </button>
        ) : null}

        <button
          type="button"
          className="hi-btn ghost"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload Photo Instead
        </button>

        {micUnavailable && !useNativeUsb && (
          <button type="button" className="hi-btn ghost" onClick={() => startCamera(deviceId)}>
            Try Again
          </button>
        )}
        {micUnavailable && useNativeUsb && (
          <button type="button" className="hi-btn ghost" onClick={() => goAnalyze(true)}>
            Continue without remaining scalp
          </button>
        )}

        <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onUpload} />
      </div>
    </div>
  );
}
